import { BackgroundGeolocation, CallbackError, Location } from "@eventr/native";
import {
  LiveLocation,
  LiveLocationPayload,
  LiveLocationSchema,
  spErrorTags,
  spLogTags,
  spWarnTags,
} from "@eventr/common";
import { io, Socket } from "socket.io-client";
import z from "zod";
import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { DEVTOOLS_ENABLED } from "../../consts";
import { useConnectivityStore } from "../../feat/connectivity/connectivity.store";
import { gatewayConfig } from "../../feat/gatewayClient/config";
import { useGuestStore } from "../../feat/guest/guest.store";
import { getClaimsJWT } from "../../feat/oauth/oauth.slice";
import { bs, useBoundStore } from "../bound.store";
import { PluginListenerHandle } from "@capacitor/core";
import { App } from "@capacitor/app";

/** Whether live location (sharing, and seeing others') is offered: not to guests, nor where the org turns it off. */
export const useIsLiveLocationAvailable = () => {
  const isGuestMode = useGuestStore((s) => s.isGuestMode);
  const disableLocationServices = useBoundStore((s) => s.orgConfig.disableLocationServices === true);
  return !isGuestMode && !disableLocationServices;
};

/** True when live location must not run: guest mode, or the org has it disabled. */
function isLiveLocationDisabled(): boolean {
  try {
    if (useGuestStore.getState().isGuestMode) return true;
    return bs().orgConfig.disableLocationServices === true;
  } catch (error) {
    // Either store can still be mid-initialisation the first time this runs.
    spWarnTags("Could not resolve live location availability", ["GPS"], error);
    return false;
  }
}

// Fused Bearing Listener
let fusedBearingListener: PluginListenerHandle | null = null;

function startFusedBearingListener() {
  if (fusedBearingListener) {
    spWarnTags("Already listening for fused bearing changes", ["GPS"]);
    return;
  }

  BackgroundGeolocation.addListener("fusedBearing", ({ bearing }) => {
    useLiveLocationStore.setState({ liveBearing: bearing });
  }).then((handle) => {
    fusedBearingListener = handle;
    spLogTags("Fused bearing listener attached", ["GPS"]);
  });
}

function stopFusedBearingListener() {
  fusedBearingListener?.remove();
  fusedBearingListener = null;
  spLogTags("Fused bearing stopped", ["GPS"]);
}

// Background tracking

// Locks
let isStarting = false;
let isStopping = false;

async function stopBackgroundTracking() {
  if (isStopping) {
    spWarnTags("Background tracking already stopping, skipping duplicate call", ["GPS"]);
    return;
  }
  isStopping = true;

  spLogTags("Stopping background location watch", ["GPS"]);

  try {
    await BackgroundGeolocation.stop();

    // ✅ Stop fused bearing listener
    stopFusedBearingListener();

    useLiveLocationStore.setState({ geolocationWatchActive: false });

    spLogTags("Background location watch stopped", ["GPS"]);
  } catch (err) {
    spErrorTags("Failed to stop background tracking", ["GPS"], err);
  } finally {
    isStopping = false;
  }
}

const START_ARGS = {
  backgroundMessage: "Sharing live location",
  backgroundTitle: "Tracking location",
  requestPermissions: true,
  stale: false,
  distanceFilter: 3,
};

async function startBackgroundTracking() {
  if (isLiveLocationDisabled()) {
    spWarnTags("Live location disabled, not starting background tracking", ["GPS"]);
    return;
  }

  if (isStarting) {
    spWarnTags("Background tracking already starting, skipping duplicate call", ["GPS"]);
    return;
  }
  isStarting = true;

  spLogTags("Starting background location watch", ["GPS"]);

  try {
    await BackgroundGeolocation.start(START_ARGS, (location?: Location, error?: CallbackError) => {
      if (error) {
        spErrorTags("BackgroundGeolocation.start error:", ["GPS"], error);
        return;
      }

      if (!location) {
        spErrorTags("BackgroundGeolocation no location", ["GPS"]);
        return;
      }

      spLogTags("User position updated.", ["GPS"], { location });

      useLiveLocationStore.setState({
        userPosition: {
          coords: {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy,
            altitude: location.altitude,
            altitudeAccuracy: location.altitudeAccuracy,
            bearing: location.bearing,
            speed: location.speed,
          },
          timestamp: location.time ?? Date.now(),
        },
      });

      emitLiveLocationFromCallback(location);
    });

    // ✅ Start fused bearing listener globally
    startFusedBearingListener();

    useLiveLocationStore.setState({ geolocationWatchActive: true });

    spLogTags("Background location watch started", ["GPS"], { ranWith: START_ARGS });
  } catch (err) {
    spErrorTags("Failed to start background tracking", ["GPS"], err);
  } finally {
    isStarting = false;
  }
}

// Lifecycle

App.addListener("resume", async () => {
  spLogTags("App resumed. Reconciling BackgroundGeolocation", ["GPS"]);

  if (isLiveLocationDisabled()) {
    spLogTags("Live location disabled, skipping reconcile", ["GPS"]);
    if (useLiveLocationStore.getState().geolocationWatchActive) await stopBackgroundTracking();
    return;
  }

  const { sharingStaleTimestamp } = useLiveLocationStore.getState();
  const isSharing = typeof sharingStaleTimestamp === "number" && sharingStaleTimestamp > Date.now();

  try {
    const state = await BackgroundGeolocation.getPluginState();
    spLogTags("Current plugin state:", ["GPS"], state);
    const { isRunning, hasCallback } = state;

    // ❌ JS says sharing is OFF → native must be OFF
    if (!isSharing) {
      if (isRunning) {
        spLogTags("Sharing stopped in background, stopping native plugin", ["GPS"]);
        await stopBackgroundTracking();
      }

      useLiveLocationStore.setState({ geolocationWatchActive: false });
      return;
    }

    // ✅ JS says sharing is ON → native must be ON
    if (!isRunning) {
      spLogTags("Sharing active but native stopped, restarting", ["GPS"]);
      await startBackgroundTracking();
      startFusedBearingListener();
    }

    // 🔁 Native running but JS lost callback
    else if (!hasCallback) {
      spLogTags("Native running without callback, reattaching", ["GPS"]);
      await startBackgroundTracking(); // lightweight reattach
      startFusedBearingListener();
    }

    useLiveLocationStore.setState({ geolocationWatchActive: true });
  } catch (e) {
    spErrorTags("Failed to reconcile BackgroundGeolocation on resume", ["GPS"], e);
  }
});

function emitLiveLocationFromCallback(location: Location) {
  const { sharingStaleTimestamp, liveLocationSocket, init } = useLiveLocationStore.getState();
  const now = Date.now();

  if (!sharingStaleTimestamp || sharingStaleTimestamp <= now) {
    spWarnTags("Live location not active or expired, skipping emit", ["GPS"]);
    if (sharingStaleTimestamp && sharingStaleTimestamp <= now) {
      spLogTags("Live location expired, stopping sharing", ["GPS"]);
      useLiveLocationStore.getState().stopSharingLiveLocation();
    }
    return;
  }

  if (!liveLocationSocket?.connected) {
    spWarnTags("Socket disconnected, re-initializing...", ["GPS"]);
    init("emit-callback"); // may not succeed immediately; next location update will retry
    return; // skip this emit
  }

  const payload: LiveLocationPayload = {
    lngLat: { lng: location.longitude, lat: location.latitude },
    staleTimestamp: sharingStaleTimestamp,
    bearing: location.bearing ?? undefined,
  };

  liveLocationSocket.emit("share", payload);
  spLogTags("Emitted live location", ["GPS"], { payload });
}

// Zod schemas
const JoinResponseSchema = z.record(z.string(), LiveLocationSchema);
const PartialResponseSchema = z.object({
  userId: z.string(),
  liveLocation: LiveLocationSchema,
});

// Store types
interface LiveLocationState {
  liveLocationSocket: Socket;
  liveLocations: Map<string, LiveLocation>;
  sharingStaleTimestamp: number | undefined;
  geolocationWatchActive: boolean;
  userPosition:
    | {
        coords: Partial<Location>;
        timestamp: number;
      }
    | undefined;
  userLocationDotClientRequired: boolean;
  /** Open `requestCurrentPosition` calls, which keep tracking on until each has a position. */
  positionRequests: number;
  connectionState: "idle" | "connecting" | "connected" | "joined";
  lastPartialAt?: number;
  liveBearing?: number | null;
  hasRehydrated: boolean;
}

interface LiveLocationActions {
  shareLiveLocation: (timeIntervalUntilStale: number) => void;
  stopSharingLiveLocation: () => void;
  joinLiveLocationRoom: () => void;
  leaveLiveLocationRoom: () => void;
  init(caller?: string): void;
}

interface LiveLocationClientStore extends LiveLocationState, LiveLocationActions {}

type PersistableLiveLocationState = Pick<LiveLocationState, "sharingStaleTimestamp" | "userPosition"> & {
  // Convert Map to serializable object for persistence
  liveLocations: Record<string, LiveLocation>;
};

export const shareIntervals = [
  { displayName: "15 Minutes", id: 15 * 60 * 1000 },
  { displayName: "1 Hour", id: 1 * 60 * 60 * 1000 },
  { displayName: "8 Hours", id: 8 * 60 * 60 * 1000 },
  { displayName: "Indefinitely", id: 100 * 24 * 60 * 60 * 1000 },
];

// Zustand store
export const useLiveLocationStore = create<LiveLocationClientStore>()(
  subscribeWithSelector(
    persist(
      devtools(
        (set, get) => {
          spLogTags("Creating live location store", ["GPS"]);

          // Actions
          async function shareLiveLocation(timeIntervalUntilStale: number) {
            const staleTimestamp = Date.now() + timeIntervalUntilStale;
            set({ sharingStaleTimestamp: staleTimestamp });
            spLogTags("📡 Sharing live location", ["GPS"], {
              duration: timeIntervalUntilStale,
              expiresAt: staleTimestamp,
            });
          }

          function stopSharingLiveLocation() {
            spLogTags("🛑 Stopping sharing live location", ["GPS"]);
            const { liveLocationSocket } = get();

            try {
              liveLocationSocket?.emit("stop-sharing", (response: boolean) => {
                if (response) {
                  set({ sharingStaleTimestamp: undefined });
                  spLogTags("Stopped sharing live location.", ["GPS"]);
                } else {
                  spLogTags("Error stopping sharing live location.", ["GPS"]);
                }
              });
            } catch (error) {
              spErrorTags("Error stopping sharing live location.", ["GPS"], error);
            }
          }

          async function joinLiveLocationRoom() {
            spLogTags("➡️ Joining live location room", ["GPS"]);
            const { liveLocationSocket } = get();
            const response = await bs().refreshTokens();

            if (response.status === "success") {
              if (!liveLocationSocket?.connected) {
                liveLocationSocket?.connect();
              }
            }

            function joinResponseHandler(response: unknown) {
              try {
                const liveLocations = JoinResponseSchema.parse(response);
                spLogTags("✅ Joined room", ["GPS"], { roomSize: Object.keys(liveLocations).length });
                set({ liveLocations: new Map(Object.entries(liveLocations)), connectionState: "joined" });
              } catch (error) {
                spErrorTags("Error parsing join response", ["GPS"], error);
              }
            }

            liveLocationSocket?.emit("join", joinResponseHandler);
          }

          function leaveLiveLocationRoom() {
            spLogTags("Leaving live location room", ["GPS"]);
            const { liveLocationSocket } = get();

            liveLocationSocket?.emit("leave", (response: boolean) => {
              if (response) {
                spLogTags("Left live location room.", ["GPS"]);
              } else {
                spLogTags("Error leaving live location room.", ["GPS"]);
              }
            });
          }

          // This gets called 2s after the Capacitor resume event.
          function init(caller?: string) {
            spLogTags("Initializing live location store", ["GPS"], { caller });
            const { liveLocationSocket } = get();

            if (!liveLocationSocket) {
              spWarnTags("init() skipped: liveLocationSocket not found", ["GPS"]);
              return;
            }

            if (liveLocationSocket?.connected) {
              spWarnTags("Socket already connected, disconnecting to re-initialize...", ["GPS"]);
              liveLocationSocket.removeAllListeners();
              liveLocationSocket.disconnect();
            }

            const disableLocationServices = bs().orgConfig.disableLocationServices === true;
            const isGuestMode = useGuestStore.getState().isGuestMode;
            const isOffline = useConnectivityStore.getState().isConnectivityAvailable === false;

            if (disableLocationServices || isGuestMode || isOffline) {
              spErrorTags("Live location disabled", ["GPS"], { disableLocationServices, isGuestMode, isOffline });
              return;
            }

            if (liveLocationSocket?.on) {
              liveLocationSocket.on("connect", onConnectionListener);
              liveLocationSocket.on("partial", onPartialListener);
              liveLocationSocket.on("disconnect", onDisconnectListener);
              liveLocationSocket.on("delete", onDeleteListener);

              liveLocationSocket.on("reconnect_attempt", (attempt: number) => {
                spLogTags(`[Socket.IO] Reconnection attempt #${attempt}`, ["GPS"]);
              });

              liveLocationSocket.on("reconnect_error", (error: Error) => {
                spErrorTags("[Socket.IO] Reconnection error:", ["GPS"], error.message);
              });

              liveLocationSocket.on("reconnect_failed", () => {
                spErrorTags("[Socket.IO] Reconnection failed. Giving up.", ["GPS"]);
              });

              liveLocationSocket.on("reconnect", (attempt: number) => {
                spLogTags(`[Socket.IO] Successfully reconnected after ${attempt} attempt(s)`, ["GPS"]);
              });

              liveLocationSocket.connect();
            } else {
              spErrorTags("liveLocationSocket not found. Setup skipped", ["GPS"]);
            }
          }

          // Socket setup
          const truncated = gatewayConfig().baseUrl.slice(0, -4);
          const SOCKET_URL = `${truncated}live-location`;
          spLogTags("Socket url:", ["GPS"], SOCKET_URL);

          const initialLiveLocationState: LiveLocationState = {
            liveLocationSocket: io(SOCKET_URL, {
              auth: liveLocationAuthenticationCallback,
              autoConnect: false,
            }),
            liveLocations: new Map(),
            sharingStaleTimestamp: undefined,
            geolocationWatchActive: false,
            userPosition: undefined,
            userLocationDotClientRequired: false,
            positionRequests: 0,
            connectionState: "idle",
            lastPartialAt: undefined,
            liveBearing: null,
            hasRehydrated: false,
          };

          const state = {
            ...initialLiveLocationState,
            shareLiveLocation,
            stopSharingLiveLocation,
            joinLiveLocationRoom,
            leaveLiveLocationRoom,
            init,
          };

          spLogTags("State 'reset'", ["GPS"], state);

          // Socket event handlers
          function onConnectionListener() {
            spLogTags("🟢 Connected to live location server", ["GPS"]);
            set({ connectionState: "connected" });

            // Only join once we're fully connected
            get().joinLiveLocationRoom();
          }

          function onDisconnectListener(reason: unknown) {
            set({ liveLocations: new Map(), connectionState: "idle" });
            spLogTags("🔴 Disconnected from live location server:", ["GPS"], { reason });
          }

          function onPartialListener(response: unknown) {
            try {
              const result = PartialResponseSchema.parse(response);

              set((prev) => ({
                liveLocations: new Map(prev.liveLocations).set(result.userId, result.liveLocation),
                lastPartialAt: Date.now(),
              }));

              spLogTags("📍 Partial location received:", ["GPS"], {
                userId: result.userId,
                location: result.liveLocation,
                total: get().liveLocations.size,
              });
            } catch (error) {
              spErrorTags("Error parsing partial response", ["GPS"], error);
            }
          }

          function onDeleteListener(response: unknown) {
            spLogTags("Delete response received", ["GPS"], { response });
            try {
              const userId = z.string().parse(response);
              set((prev) => {
                const liveLocations = new Map(prev.liveLocations);
                liveLocations.delete(userId);
                return { liveLocations };
              });
            } catch (error) {
              spErrorTags("Error parsing delete response", ["GPS"], error);
            }
          }

          return state;
        },
        { enabled: DEVTOOLS_ENABLED },
      ),
      {
        name: "live-location-store",

        partialize: ({ sharingStaleTimestamp, userPosition, liveLocations }) => ({
          sharingStaleTimestamp,
          userPosition,
          liveLocations: Object.fromEntries(liveLocations),
        }),

        // Hydration has finished whatever storage held. With nothing stored (a fresh install, or
        // cleared storage) persist still calls this, with undefined, and the tracking subscriber
        // waits on `hasRehydrated`, so leaving it false meant a share never started tracking.
        merge: (persistedState, currentState) => {
          if (!persistedState) return { ...currentState, hasRehydrated: true };

          try {
            const persisted = persistedState as PersistableLiveLocationState;

            return {
              ...currentState,
              ...persisted,
              liveLocations: new Map(persisted.liveLocations ? Object.entries(persisted.liveLocations) : []),
              hasRehydrated: true,
            };
          } catch (err) {
            spErrorTags("Failed to merge persisted state:", ["GPS"], err);
            return { ...currentState, hasRehydrated: true };
          }
        },
      },
    ),
  ),
);

export const lls = useLiveLocationStore.getState;

// Socket authentication callback
async function liveLocationAuthenticationCallback(cb: (data: object) => void) {
  spLogTags("Retrieving tokens for live location socket.", ["GPS"]);
  const bearerToken = bs().IDtoken;
  const claimsToken = getClaimsJWT();
  cb({ bearerToken, claimsToken });
}

// Background geolocation watcher
useLiveLocationStore.subscribe(
  (state) => ({
    sharingStaleTimestamp: state.sharingStaleTimestamp,
    isMapVisible: state.userLocationDotClientRequired,
    wantsPosition: state.positionRequests > 0,
    isTracking: state.geolocationWatchActive,
    hasRehydrated: state.hasRehydrated,
  }),
  async ({ sharingStaleTimestamp, isMapVisible, wantsPosition, isTracking, hasRehydrated }) => {
    spLogTags("Live location state changed", ["GPS"], {
      sharingStaleTimestamp,
      isMapVisible,
      wantsPosition,
      isTracking,
      hasRehydrated,
    });

    if (!hasRehydrated) {
      spWarnTags("Store hasn't finished hydrating yet", ["GPS"]);
      return;
    }

    if (isLiveLocationDisabled()) {
      spLogTags("Live location disabled, not tracking", ["GPS"]);
      if (isTracking) await stopBackgroundTracking();
      return;
    }

    const isSharing = typeof sharingStaleTimestamp === "number" && sharingStaleTimestamp > Date.now();

    if ((isSharing || isMapVisible || wantsPosition) && !isTracking) {
      await startBackgroundTracking();
    }

    if (!isSharing && !isMapVisible && !wantsPosition && isTracking) {
      await stopBackgroundTracking();
    }
  },
  { equalityFn: shallow, fireImmediately: true },
);

/** A position no older than this answers `requestCurrentPosition` at once. */
const RECENT_POSITION_MS = 30 * 1000;

/**
 * The device's position, once: a recent one if tracking already has it, otherwise the next
 * fix. Tracking runs only while the request is open, through the same watcher a live share
 * or the map uses, so neither of those is interrupted. Rejects when live location is off for
 * this org or guest mode, or with no fix within `timeoutMs`.
 */
export function requestCurrentPosition(timeoutMs = 20 * 1000): Promise<{ latitude: number; longitude: number }> {
  if (isLiveLocationDisabled()) return Promise.reject(new Error("Live location is disabled"));

  const { userPosition, geolocationWatchActive } = useLiveLocationStore.getState();
  const { latitude, longitude } = userPosition?.coords ?? {};
  if (
    geolocationWatchActive &&
    userPosition &&
    Date.now() - userPosition.timestamp <= RECENT_POSITION_MS &&
    typeof latitude === "number" &&
    typeof longitude === "number"
  ) {
    return Promise.resolve({ latitude, longitude });
  }

  return new Promise((resolve, reject) => {
    const previous = useLiveLocationStore.getState().userPosition;
    const finish = () => {
      clearTimeout(timer);
      unsubscribe();
      useLiveLocationStore.setState((s) => ({ positionRequests: Math.max(0, s.positionRequests - 1) }));
    };
    const timer = setTimeout(() => {
      finish();
      reject(new Error("No position fix"));
    }, timeoutMs);
    const unsubscribe = useLiveLocationStore.subscribe(
      (s) => s.userPosition,
      (position) => {
        const { latitude, longitude } = position?.coords ?? {};
        if (position === previous || typeof latitude !== "number" || typeof longitude !== "number") return;
        finish();
        resolve({ latitude, longitude });
      },
    );
    useLiveLocationStore.setState((s) => ({ positionRequests: s.positionRequests + 1 }));
  });
}

// Tears live location down on entering guest mode, since the guards above only refuse new work.
useGuestStore.subscribe(
  (state) => state.isGuestMode,
  async (isGuestMode) => {
    if (!isGuestMode) return;

    spLogTags("Guest mode entered, disabling live location", ["GPS"]);

    const { liveLocationSocket, geolocationWatchActive } = useLiveLocationStore.getState();

    if (geolocationWatchActive) await stopBackgroundTracking();

    if (liveLocationSocket?.connected) {
      // Listeners are reattached by init() if the user leaves guest mode and signs in.
      liveLocationSocket.removeAllListeners();
      liveLocationSocket.disconnect();
    }

    useLiveLocationStore.setState({
      sharingStaleTimestamp: undefined,
      liveLocations: new Map(),
      userLocationDotClientRequired: false,
      connectionState: "idle",
    });
  },
);
